import React from "react";
import {
  AbsoluteFill,
  Sequence,
  useVideoConfig,
  Audio,
  staticFile,
} from "remotion";
import { Background } from "../components/Background";
import { AnimatedText } from "../components/AnimatedText";
import { SanskritVerse } from "../components/SanskritVerse";
import { HookCard } from "../components/HookCard";
import { SeriesBranding } from "../components/SeriesBranding";
import { CallToAction } from "../components/CallToAction";
import { secondsToFrames } from "../lib/parse-vtt";
import { SERIES_THEMES, LAYOUT, type Platform, type Format } from "../styles/theme";
import type { Clip } from "../lib/segment-episode";

export interface SutraClipProps {
  clip: Clip;
  platform: Platform;
  format: Format;
  language: "en" | "de";
  audioSrc?: string;
}

export const SutraClip: React.FC<SutraClipProps> = ({
  clip,
  platform,
  format,
  language,
  audioSrc,
}) => {
  const { fps, durationInFrames } = useVideoConfig();
  const theme = SERIES_THEMES[clip.seriesSlug] || SERIES_THEMES["patanjali-yoga-sutras-2019"];
  const layout = LAYOUT[format];

  const episodeLabel = `${clip.episodeCode} ${String(clip.episodeNumber).padStart(2, "0")}`;

  // Calculate CTA start (last 4 seconds, but not on TikTok)
  const ctaFrames = platform === "tiktok" ? 0 : fps * 4;
  const ctaStart = durationInFrames - ctaFrames;

  // Hook card duration (first 2 seconds)
  const hookDuration = fps * 2;

  return (
    <AbsoluteFill>
      {/* Background - full duration */}
      <Background
        theme={theme}
        variant={clip.isScriptureCard ? "deep" : "warm"}
      />

      {/* Series branding - top left */}
      <SeriesBranding
        seriesName={theme.name}
        episodeLabel={episodeLabel}
        format={format}
        accentColor={theme.accentColor}
      />

      {/* Hook card - first 2 seconds */}
      <Sequence from={0} durationInFrames={hookDuration}>
        <AbsoluteFill
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <HookCard
            text={clip.hookLine}
            fontSize={layout.fontSize.hook}
            accentColor={theme.accentColor}
            durationFrames={hookDuration}
          />
        </AbsoluteFill>
      </Sequence>

      {/* Main content - subtitle cues */}
      <Sequence from={hookDuration}>
        <AbsoluteFill
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: format === "portrait" ? "0 60px" : "0 200px",
          }}
        >
          {clip.cues.map((cue, i) => {
            // Offset cue times relative to clip start
            const cueStartSec = cue.startTime - clip.startTime;
            const cueDurSec = cue.endTime - cue.startTime;
            const cueStartFrame = secondsToFrames(cueStartSec, fps);
            const cueDurFrames = secondsToFrames(cueDurSec, fps);

            return (
              <Sequence
                key={cue.index}
                from={cueStartFrame}
                durationInFrames={cueDurFrames}
              >
                <AbsoluteFill
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: format === "portrait" ? "0 40px" : "0 160px",
                  }}
                >
                  {clip.isScriptureCard && i === 0 ? (
                    <SanskritVerse
                      text={cue.text}
                      startFrame={0}
                      durationFrames={cueDurFrames}
                      fontSize={layout.fontSize.scripture}
                      accentColor={theme.accentColor}
                    />
                  ) : (
                    <AnimatedText
                      text={cue.text}
                      startFrame={0}
                      durationFrames={cueDurFrames}
                      style={i === 0 ? "fade-in" : "word-reveal"}
                      fontSize={layout.fontSize.body}
                      accentColor={theme.accentColor}
                      textAlign="center"
                    />
                  )}
                </AbsoluteFill>
              </Sequence>
            );
          })}
        </AbsoluteFill>
      </Sequence>

      {/* Call to action - last 4 seconds (not on TikTok) */}
      {ctaFrames > 0 && (
        <Sequence from={ctaStart} durationInFrames={ctaFrames}>
          <CallToAction
            platform={platform}
            format={format}
            accentColor={theme.accentColor}
          />
        </Sequence>
      )}

      {/* Audio track (if provided) */}
      {audioSrc && (
        <Audio
          src={audioSrc}
          startFrom={secondsToFrames(clip.startTime, fps)}
          endAt={secondsToFrames(clip.endTime, fps)}
        />
      )}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
